import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { PreInitSettingsService } from '../services/settings/pre-init-setting.service';
import { format } from 'date-fns';
import { LoaderManagerService } from '../services/loader-manager.service';
import { ADPConfigService, StorageService as ADPStorageService } from 'digital-core';
import { StorageService } from '../services/storage.service';
import { UserSessionTrackingService } from '../services/user-session-tracking.service';
import { isEmpty as _isEmpty } from 'lodash';
import { AppConstants } from '../constants';

export const OKTA_TOKEN_STORAGE = 'okta-token-storage';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
	middlewareUrl: string;
	accountPath: string;
	isProduction: string;
  subscriptionKey: string;
	private userSessionTracking = inject(UserSessionTrackingService);

	constructor(private preInitSettingsService: PreInitSettingsService,
    public loaderManagerService: LoaderManagerService,
    public configService: ADPConfigService,
    public storageService: ADPStorageService,
		private pghStorageService: StorageService,
    ) {
		this.preInitSettingsService.settings.subscribe(settings =>
      this.subscriptionKey = settings?.middleWare?.subscriptionKey
    );
	}

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const token: any = JSON.parse(localStorage.getItem(OKTA_TOKEN_STORAGE));
		const identityToken: any = localStorage.getItem(AppConstants.STORAGE_KEYS.IdentityToken);
		if (!this.middlewareUrl) {
      this.middlewareUrl = this.preInitSettingsService.getSettings()?.middleWare?.middlewareUrl?.toLowerCase();
		}
		if (!this.accountPath) {
			this.accountPath = this.preInitSettingsService.getSettings()?.middleWare?.accountPath?.toLowerCase();
		}
		if (!this.isProduction) {
			this.isProduction = this.preInitSettingsService.getSettings()?.isProduction?.toLowerCase();
		}
    this.loaderManagerService.onRequestStart(request);

		let requestId = '';
		let isMiddlewareUrl = false;
		if (this.middlewareUrl && request.url.toLowerCase().startsWith(this.middlewareUrl) && !request.url.toLowerCase().endsWith('appsettings.json')) {
			let name = this.preInitSettingsService.getSiteSettings().name;
			requestId = this.getApiRequestId(name);
			isMiddlewareUrl = true;
		}

		if (token && (token.hasOwnProperty('accessToken') || token.hasOwnProperty('access_token'))) {
			let accessToken = '';
			if (token.hasOwnProperty('access_token')) {
        accessToken = token['access_token'].accessToken;
      }
			if (token.hasOwnProperty('accessToken')) {
        accessToken = token['accessToken'].accessToken;
      }
			if (requestId && isMiddlewareUrl) {
        let editor = this.storageService.getLocal<string>(AppConstants.STORAGE_KEYS.ContentEditorId) ?? '';
				let culture = this.pghStorageService.getLocal<string>('language');
			  culture = _isEmpty(culture) ? 'en' : culture; // Default to 'en'

				request = request.clone({
					setHeaders: {
						Authorization: 'Bearer ' + accessToken,
						'X-Api-Request-Id': requestId,
						[AppConstants.HEADERS.UserSessionId]: this.userSessionTracking.getSessionId(),
            'Content-Editor-Id': editor,
            ClientCMS: this.configService.clientCMS,
            ParentCMS: this.configService.parentCMS,
						culture: culture
					}
				});
			}
		} else if (requestId && isMiddlewareUrl) {
      let editor = this.storageService.getLocal<string>(AppConstants.STORAGE_KEYS.ContentEditorId) ?? '';
			let culture = this.pghStorageService.getLocal<string>('language');
			culture = _isEmpty(culture) ? 'en' : culture; // Default to 'en'

			request = request.clone({
				setHeaders: {
					'X-Api-Request-Id': requestId,
					[AppConstants.HEADERS.UserSessionId]: this.userSessionTracking.getSessionId(),
          'Content-Editor-Id': editor,
          ClientCMS: this.configService.clientCMS,
          ParentCMS: this.configService.parentCMS,
					culture: culture
				}
			});
		}

		let elitaEnv = this.pghStorageService.getLocal<string>('elita-environment') ?? this.pghStorageService.getLocal<string>('elita-environment-default');

		if (elitaEnv && this.isProduction == 'false' && request.url.toLowerCase().startsWith(`${this.middlewareUrl}${this.accountPath}`)) {
			request = request.clone({
        setHeaders: {
          'Elita-Environment': elitaEnv
        }
      });
		}

		if (this.subscriptionKey && isMiddlewareUrl) {
      request = request.clone({
        setHeaders: {
          'Ocp-Apim-Subscription-Key': this.subscriptionKey
        }
      });
    }

		if (identityToken  && isMiddlewareUrl) {
			request = request.clone({
				setHeaders: {
					'IdentityToken': identityToken
				}
			});
		}

		return next.handle(request).pipe(tap({
			next: (event) => {
				if (event instanceof HttpResponse && event.headers.get(AppConstants.HEADERS.IdentityToken)) {
					this.updateIdentityTokenFromResponse(event);
				}
			},
      finalize: () => {
        this.loaderManagerService.onRequestStop(request);
      }
   	}));
	}

	private updateIdentityTokenFromResponse(event: HttpResponse<any>) {
		const currentIdentityToken = localStorage.getItem(AppConstants.STORAGE_KEYS.IdentityToken);
		const newIdentityToken = event.headers.get(AppConstants.HEADERS.IdentityToken);
		if(!currentIdentityToken) {
			localStorage.setItem(AppConstants.STORAGE_KEYS.IdentityToken, newIdentityToken ?? '');
			return;
		}
		
		//decode the tokens
		const decodeToken = (token: string) => {
			try {
				return JSON.parse(atob(token.split('.')[1]));
			} catch (e) {
				console.error('Failed to decode token', e);
				return null;
			}
		}
		const currentTokenPayload = decodeToken(currentIdentityToken ?? '');
		const newTokenPayload = decodeToken(newIdentityToken ?? '');

		//validate the new token payload has more CertificateNumbers than the current token payload before replacing it
		if (currentTokenPayload && newTokenPayload) {
			const currentCerts = currentTokenPayload['CertificateNumbers'] || [];
			const newCerts = newTokenPayload['CertificateNumbers'] || [];
			if (newCerts.length >= currentCerts.length) {
				localStorage.setItem(AppConstants.STORAGE_KEYS.IdentityToken, newIdentityToken ?? '');
			}
		}
	}
	
	private getApiRequestId(name: string) {
    let reqId = `${name}-${format(new Date(), "yyyyMMdd-HHmmssSSS")}`;
    return reqId;
  }
}
